import { Button } from "@ui/button";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "@ui/menubar";
import { MenuIcon } from "@components/icons/MenuIcon";
import TranslatedText from "@components/items/display/TranslatedText";
import { setAlert, useAppSelector, useFavorite } from "@lib/stores";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import { useLanguage } from "@modules/localizations/LanguageContext";
import { ArrowUpLeft, Heart, Info, PlusCircle, Share } from "lucide-react";
import { useGetDynamicLink } from "@lib/stores/content/hooks";
import { AlertType } from "@lib/stores/common/types";
import { translate } from "@modules/localizations/translate";
import { Server } from "@wajeez/wajeez.microservices";
import { useLocation } from "react-router-dom";
import CurrentPlaylists from "../my-corner/CurrentPlaylists";
import { useState } from "react";
import useCurrentPlan from "@utils/hooks/useCurrentPlan";
import { SVG } from "@components/items/common/SVG";
import { useAppLiveQuery } from "@modules/local/hooks";
import { db } from "@modules/local/db";
import { detailsPageType } from "@utils/helpers/detailsPageType";

const PlayerSettingMenu = ({
  content,
}: {
  content: Server.ContentEntityOutput | undefined;
}) => {
  const navigate = useAppNavigate();
  const { pathname } = useLocation();
  const { direction } = useLanguage();
  const { data: dynamicLink } = useGetDynamicLink();
  const { plan } = useCurrentPlan();
  const { isFavorite, handleFavoriteClick } = useFavorite(content);
  const [isCurrentPlaylistOpen, setIsCurrentPlaylistOpen] =
    useState<boolean>(false);
  const user = useAppSelector((state) => state.auth.user);
  const { result: flow } = useAppLiveQuery(
    () => db.readerFlows.get(user.id || ""),
    [user.id]
  );

  const typeNavigation = flow?.typeNavigations?.find(
    (t) => t.contentEntityTypeId === content?.contentEntityTypeId
  );

  const isHasDetailsPage =
    typeNavigation?.detailsPageId === typeNavigation?.playerPageId &&
    !detailsPageType(typeNavigation?.detailsPageType || "");

  const onShareClick = () => {
    if (navigator && dynamicLink?.link) {
      navigator.clipboard.writeText(dynamicLink.link);

      setAlert({
        show: true,
        for: "page",
        type: AlertType.SUCCESS,
        messages: [
          {
            code: 0,
            field: null,
            message: translate(
              "content-details.messages.the link has been copied successfully"
            ),
          },
        ],
      });
    }
  };

  return (
    <>
      <CurrentPlaylists
        contentId={content?.id}
        isOpen={isCurrentPlaylistOpen}
        setOpen={setIsCurrentPlaylistOpen}
      />
      <Menubar className="border-none" dir={direction}>
        <MenubarMenu>
          <MenubarTrigger
            asChild
            className="cursor-pointer rounded-none focus:bg-transparent focus-visible:ring-0 data-[state=open]:bg-transparent"
          >
            <Button
              variant="basic"
              className="gap-x-10 text-black dark:text-white"
            >
              <MenuIcon width={34} height={6} />
            </Button>
          </MenubarTrigger>
          <MenubarContent
            sideOffset={25}
            align="end"
            className="bg-[#F3F3F3] dark:bg-black dark:text-white"
          >
            <MenubarItem
              className="group flex w-full cursor-pointer items-center justify-between border-b border-b-white hover:bg-white dark:hover:text-black"
              onSelect={handleFavoriteClick}
            >
              {isFavorite ? (
                <TranslatedText text={`player.${"remove from favorites"}`} />
              ) : (
                <TranslatedText text={`player.${"add to favorites"}`} />
              )}
              <Heart
                width={20}
                height={20}
                className={`${isFavorite && "fill-black dark:fill-white dark:group-hover:fill-black"}`}
              />
            </MenubarItem>
            <MenubarItem
              className="group flex w-full cursor-pointer items-center justify-between border-b border-b-white hover:bg-white dark:hover:text-black"
              onClick={() => {
                if (!plan?.isFree) {
                  setIsCurrentPlaylistOpen(true);
                }
              }}
            >
              {plan?.isFree ? (
                <a
                  href={
                    "https://pay.wajeez.com/?content=registerMethod&subscriptionType=P1Y"
                  }
                  rel="noopener noreferrer"
                  className="flex w-full items-center justify-between gap-x-4 py-2"
                >
                  <div>
                    <TranslatedText
                      text={`${"content-details"}.${"book-menu"}.${"add-to-playlist"}`}
                    />
                  </div>
                  <div>
                    <SVG src="/assets/icons/crown.svg" className="h-6 w-6" />
                  </div>
                </a>
              ) : (
                <>
                  <TranslatedText text={`player.${"add to playlist"}`} />
                  <PlusCircle size={20} />
                </>
              )}
            </MenubarItem>
            <MenubarItem
              className="group flex w-full cursor-pointer items-center justify-between border-b border-b-white hover:bg-white dark:hover:text-black"
              onSelect={onShareClick}
            >
              <TranslatedText text={`player.${"share"}`} />
              <Share size={20} />
            </MenubarItem>
            <MenubarItem
              className="group flex w-full cursor-pointer items-center justify-between border-b border-b-white hover:bg-white dark:hover:text-black"
              onSelect={() => {
                navigate("/settings", {
                  tab: "content",
                });
              }}
            >
              <TranslatedText text={`player.${"listening-settings"}`} />
              <ArrowUpLeft size={20} />
            </MenubarItem>
            {isHasDetailsPage &&
              !pathname.includes(`/details/${content?.id}`) && (
                <MenubarItem
                  className="group flex w-full cursor-pointer items-center justify-between hover:bg-white dark:hover:text-black"
                  onSelect={() => {
                    navigate(`/details/${content?.id}`);
                  }}
                >
                  <TranslatedText text={`player.${"show-contnt-details"}`} />
                  <Info size={20} />
                </MenubarItem>
              )}
          </MenubarContent>
        </MenubarMenu>
      </Menubar>
    </>
  );
};

export default PlayerSettingMenu;
